import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from './AuthContext'

export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const THEME_STORAGE_KEY = 'asiliflow-theme-preference'
const LIGHT_THEME_COLOR = '#0d9488'
const DARK_THEME_COLOR = '#020617'

interface ThemeContextValue {
  themePreference: ThemePreference
  resolvedTheme: ResolvedTheme
  setThemePreference: (preference: ThemePreference) => void
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system'
}

function readStoredThemePreference(): ThemePreference | null {
  if (typeof window === 'undefined') return null

  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    return isThemePreference(stored) ? stored : null
  } catch {
    return null
  }
}

function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? getSystemTheme() : preference
}

// Public marketing/auth routes that must always render in light mode,
// regardless of the authenticated user's theme preference.
const PUBLIC_LIGHT_MODE_ROUTES = [
  '/',
  '/about',
  '/contact',
  '/documentation',
  '/help',
  '/login',
  '/signup',
  '/forgot-password',
  '/accept-invitation',
  '/privacy',
  '/terms',
  '/status',
  '/whats-new',
  '/sign-poc',
]

function isPublicLightModeRoute(pathname: string): boolean {
  // Exact match for '/' to avoid matching every route
  if (pathname === '/') return true
  return PUBLIC_LIGHT_MODE_ROUTES.some(route => route !== '/' && pathname.startsWith(route))
}

function applyTheme(preference: ThemePreference) {
  if (typeof document === 'undefined') return

  const root = document.documentElement
  const themeColorMeta = document.querySelector('meta[name="theme-color"]')

  // Force light mode on public routes (marketing site, auth flows)
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/'
  const forceLight = isPublicLightModeRoute(pathname)

  const resolvedTheme = forceLight ? 'light' : resolveTheme(preference)

  root.classList.toggle('dark', resolvedTheme === 'dark')
  root.dataset.theme = forceLight ? 'light' : preference
  root.style.colorScheme = resolvedTheme

  if (themeColorMeta) {
    themeColorMeta.setAttribute('content', resolvedTheme === 'dark' ? DARK_THEME_COLOR : LIGHT_THEME_COLOR)
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const location = useLocation()
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() => readStoredThemePreference() ?? 'system')

  const setThemePreference = useCallback((preference: ThemePreference) => {
    setThemePreferenceState(preference)

    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, preference)
      } catch {
        // Ignore localStorage failures
      }
    }
  }, [])

  useEffect(() => {
    if (readStoredThemePreference()) return

    if (isThemePreference(user?.themePreference)) {
      setThemePreference(user.themePreference)
    }
  }, [setThemePreference, user?.themePreference])

  // Re-apply theme on preference change AND on route change.
  // Public routes force light mode; app routes honor the user's preference.
  useEffect(() => {
    applyTheme(themePreference)
  }, [themePreference, location.pathname])

  useEffect(() => {
    if (typeof window === 'undefined' || themePreference !== 'system') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => applyTheme('system')

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [themePreference])

  const value = useMemo<ThemeContextValue>(() => ({
    themePreference,
    resolvedTheme: resolveTheme(themePreference),
    setThemePreference,
  }), [setThemePreference, themePreference])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const context = useContext(ThemeContext)

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }

  return context
}
